/**
 * Seguimiento de lecturas de Firestore desde el cliente.
 *
 * Responde a la pregunta "¿quién leyó qué?": Cloud Monitoring no tiene
 * dimensión de tenant y los audit logs de Data Access están apagados, así que
 * la única forma exacta de atribuir es que el propio cliente informe de lo que
 * lee. Aquí se registra cuántos documentos devolvió cada consulta, con el RUC y
 * el usuario, y se agrupa en un solo envío para no inflar las escrituras.
 *
 * Diseño:
 *  - Los eventos se acumulan en memoria y se envían en un ÚNICO documento por
 *    ráfaga (debounce). Un usuario que abre el dashboard genera 1 escritura,
 *    no 6.
 *  - Todo el flujo es "fire and forget": si falla, no interrumpe la pantalla.
 *  - La identidad no la manda el cliente: el servidor la sella a partir del
 *    token de Firebase Auth (ver functions/metricasConsumo.js).
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import firebaseApp from "../firebase/credenciales";

const functions = getFunctions(firebaseApp);

const DEBOUNCE_MS = 45_000;
const MAX_EVENTOS_POR_ENVIO = 200;

let origenActual = "app";
const buffers = new Map(); // origen -> { origen, colecciones, documentos, eventos }
let timer = null;
let envioEnCurso = false;
let fallosConsecutivos = 0;

/** Fija la pantalla/origen que se está midiendo (p. ej. "dashboard:indicadores"). */
export function establecerOrigenConsumo(origen) {
  origenActual = String(origen || "app").slice(0, 60);
}

/**
 * Devuelve el origen vigente.
 *
 * Se usa para "fotografiar" el origen en el momento de LANZAR una consulta y
 * no cuando la consulta termina: como el origen es global, varias pantallas
 * pueden estar cargando a la vez y el último `establecerOrigenConsumo` en
 * ganaría la atribución de todas las lecturas pendientes.
 */
export function obtenerOrigenConsumo() {
  return origenActual;
}

/**
 * Registra una lectura.
 * @param {Record<string, number>} colecciones  p. ej. { registros: 4160, users: 602 }
 * @param {{ origen?: string }} [opciones]
 */
export function registrarLecturas(colecciones, opciones = {}) {
  try {
    if (!colecciones || typeof colecciones !== "object") return;
    const origen = String(opciones.origen || origenActual).slice(0, 60);

    let documentos = 0;
    const detalle = {};
    for (const [col, n] of Object.entries(colecciones)) {
      const valor = Number(n || 0);
      if (!Number.isFinite(valor) || valor <= 0) continue;
      detalle[col] = valor;
      documentos += valor;
    }
    if (documentos <= 0) return;

    const buffer = buffers.get(origen) || { origen, colecciones: {}, documentos: 0, eventos: 0 };
    buffer.documentos += documentos;
    buffer.eventos += 1;
    for (const [col, valor] of Object.entries(detalle)) {
      buffer.colecciones[col] = (buffer.colecciones[col] || 0) + valor;
    }
    buffers.set(origen, buffer);

    programarEnvio();
  } catch {
    // El tracking nunca debe romper la app.
  }
}

function programarEnvio() {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    enviar();
  }, DEBOUNCE_MS);
}

/** Fuerza el envío de lo acumulado (p. ej. al ocultar la pestaña). */
export async function enviarLecturasPendientes() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  await enviar();
}

async function enviar() {
  if (envioEnCurso || buffers.size === 0) return;
  envioEnCurso = true;

  // Se envía un documento por origen. En el uso normal es 1-3 por usuario.
  const lote = [...buffers.values()];
  buffers.clear();

  try {
    const callable = httpsCallable(functions, "registrarConsumoLecturas");
    for (const pendiente of lote) {
      await callable({
        origen: pendiente.origen,
        colecciones: pendiente.colecciones,
        documentos: Math.min(pendiente.documentos, 1_000_000_000),
        eventos: Math.min(pendiente.eventos, MAX_EVENTOS_POR_ENVIO),
      });
    }
    fallosConsecutivos = 0;
  } catch (error) {
    fallosConsecutivos += 1;
    // Reagrupa lo perdido para reintentar en el siguiente ciclo.
    if (fallosConsecutivos <= 3) {
      for (const pendiente of lote) {
        const buffer = buffers.get(pendiente.origen) || { origen: pendiente.origen, colecciones: {}, documentos: 0, eventos: 0 };
        buffer.documentos += pendiente.documentos;
        buffer.eventos += pendiente.eventos;
        for (const [col, valor] of Object.entries(pendiente.colecciones)) {
          buffer.colecciones[col] = (buffer.colecciones[col] || 0) + valor;
        }
        buffers.set(pendiente.origen, buffer);
      }
    } else if (fallosConsecutivos === 1) {
      console.warn("consumoLecturas: no se pudo registrar (se descarta hasta el siguiente ciclo).");
    }
  } finally {
    envioEnCurso = false;
  }
}

// Envía lo acumulado si la pestaña se oculta o se cierra.
if (typeof document !== "undefined" && typeof window !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") enviarLecturasPendientes();
  });
  window.addEventListener("pagehide", () => enviarLecturasPendientes());
}
